import { useCallback, useRef, useState } from 'react';
import logger from '../utils/logger';
import { RECORDING_FLAGS } from '../constants/recordingFlags';
import { blankRecordingService, BlankRecordingLayout, StreamSegment, AudioStreamSegment } from '../services/blankRecordingService';
import { separateStreamUploader, StreamUploadProgress } from '../services/separateStreamUploader';
import { projectsManager } from '../services/projectsManager';
import {
  getSupportedMimeType,
  BITRATES_BY_QUALITY,
  AUDIO_BITRATE,
  SEPARATE_STREAM_TIMESLICE,
} from '../utils/browserCompatibility';
import type { RecordingQuality } from '../context/RecorderContext';
import { 
  savePendingRecording, 
  deletePendingRecording, 
  getPendingRecordingBySceneId,
  deleteAllPendingRecordingsForScene,
  IPendingRecordingRow 
} from '../idb/chunk-db';
import { blankSessionPersistence } from '../services/blankSessionPersistence';

// Track which streams are currently active
export interface StreamActiveState {
  screen: boolean;
  webcam: boolean;
  audio: boolean; // Dedicated audio stream active state
}

export interface SegmentBlobUrl {
  segmentIndex: number;
  blobUrl: string;
}

export interface PendingRecording {
  screenBlobUrl: string | null;
  webcamBlobUrl: string | null;
  audioBlobUrl: string | null; // Dedicated audio stream blob URL
  combinedBlobUrl: string | null; // Combined video for preview (first screen or webcam)
  duration: number;
  micEnabled: boolean;
  hasDedicatedAudioStream: boolean; // Whether this recording uses separate audio stream
  layout: BlankRecordingLayout;
  sceneId: string | null;
  // Per-segment blob URLs for multi-segment recordings (mid-recording toggles)
  screenSegmentBlobUrls?: SegmentBlobUrl[];
  webcamSegmentBlobUrls?: SegmentBlobUrl[];
  audioSegmentBlobUrls?: SegmentBlobUrl[]; // Per-segment audio blob URLs
}

export interface SeparateStreamRecorderState {
  isRecording: boolean;
  isPaused: boolean;
  recordingId: string | null;
  recordingTime: number;
  uploadProgress: {
    screen: StreamUploadProgress | null;
    webcam: StreamUploadProgress | null;
    audio: StreamUploadProgress | null;
  };
  error: string | null;
  streamActive: StreamActiveState; // Which streams are currently active
  pendingRecording: PendingRecording | null; // For scene mode: holds blobs until user confirms
}

export interface SeparateStreamRecorderCallbacks {
  onUploadStart?: () => void;
  onUploadProgress?: (progress: { screen: StreamUploadProgress | null; webcam: StreamUploadProgress | null }) => void;
  onUploadComplete?: (recordingId: string) => void;
  onUploadError?: (error: string) => void;
}

export interface UseSeparateStreamRecorderResult {
  state: SeparateStreamRecorderState;
  startRecording: (options: {
    screenStream: MediaStream | null;
    webcamStream: MediaStream | null;
    micStream: MediaStream | null;
    systemAudioStream: MediaStream | null;
    layout: BlankRecordingLayout;
    quality: RecordingQuality;
    sceneId?: string; // Optional scene ID for scene-based recording
    projectId?: string; // Optional project ID for scene-based recording
    roundedWebcam?: boolean; // Whether webcam is circular/rounded
    mirroredWebcam?: boolean; // Whether webcam is horizontally mirrored
    avatarWebcam?: boolean; // Whether using avatar image instead of webcam video
    avatarUrl?: string; // URL of avatar image (when avatarWebcam is true)
    edgeToEdge?: boolean; // Whether edge-to-edge mode is active (no padding, sharp corners)
  }) => Promise<void>;
  stopRecording: () => Promise<PendingRecording | null>; // Returns pending recording for scene mode
  uploadRecording: () => Promise<any>; // Uploads pending recording and returns metadata (for scene mode after user confirms)
  uploadFromRecoveredData: (recoveredData: any) => Promise<void>; // Uploads recovered recording from IndexedDB
  clearPendingRecording: () => void; // Clears pending recording (for re-record)
  abortRecording: () => Promise<void>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  getRecordingTime: () => number;
  toggleStream: (
    streamType: 'screen' | 'webcam',
    active: boolean,
    options?: {
      micStream?: MediaStream | null;
      position?: { x: number; y: number; width: number; height: number };
      stream?: MediaStream | null; // Stream to use when activating
      roundedWebcam?: boolean; // Update webcam shape during recording
      mirroredWebcam?: boolean; // Update webcam mirror state during recording
      avatarWebcam?: boolean; // Whether using avatar image instead of webcam video
      avatarUrl?: string; // URL of avatar image
    }
  ) => Promise<void>;
  updateBackgroundKey: (imageKey: string) => void;
}

async function uploadAvatarSegmentsToS3(
  segments: StreamSegment[],
  projectId: string | number
): Promise<void> {
  const seen = new Set<string>();
  for (const seg of segments) {
    if (seg.isAvatar && seg.avatarUrl && seg.avatarUrl.startsWith('http')) {
      if (!seen.has(seg.avatarUrl)) {
        try {
          const s3Key = await projectsManager.uploadAvatarToS3(projectId, seg.avatarUrl);
          const originalUrl = seg.avatarUrl;
          for (const s of segments) {
            if (s.avatarUrl === originalUrl) {
              s.avatarUrl = s3Key;
            }
          }
          seen.add(originalUrl);
          logger.info('[SeparateStreamRecorder] Avatar uploaded to S3', { s3Key });
        } catch (err) {
          logger.warn('[SeparateStreamRecorder] Avatar S3 upload failed (non-critical), keeping external URL', err);
        }
      }
    }
  }
}

export function useSeparateStreamRecorder(callbacks?: SeparateStreamRecorderCallbacks): UseSeparateStreamRecorderResult {
  const [state, setState] = useState<SeparateStreamRecorderState>({
    isRecording: false,
    isPaused: false,
    recordingId: null,
    recordingTime: 0,
    uploadProgress: { screen: null, webcam: null, audio: null },
    error: null,
    streamActive: { screen: false, webcam: false, audio: false },
    pendingRecording: null,
  });

  const screenRecorderRef = useRef<MediaRecorder | null>(null);
  const webcamRecorderRef = useRef<MediaRecorder | null>(null);
  const audioRecorderRef = useRef<MediaRecorder | null>(null); // Dedicated audio recorder
  const screenChunksRef = useRef<Blob[]>([]);
  const webcamChunksRef = useRef<Blob[]>([]);
  const audioChunksRef = useRef<Blob[]>([]); // Dedicated audio chunks
  const recordingStartTimeRef = useRef<number | null>(null);
  const pauseStartTimeRef = useRef<number | null>(null);
  const totalPausedDurationRef = useRef<number>(0);
  const recordingIdRef = useRef<string | null>(null);
  const uploadUrlsRef = useRef<{
    screen: { url: string; key: string };
    webcam: { url: string; key: string };
  } | null>(null);
  const layoutRef = useRef<BlankRecordingLayout | null>(null);
  const micEnabledRef = useRef<boolean>(false);
  const screenHasAudioRef = useRef<boolean>(false);
  const webcamHasAudioRef = useRef<boolean>(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  
  // Stream segments for mid-recording toggles - each segment has its own video
  const screenSegmentsRef = useRef<StreamSegment[]>([]);
  const webcamSegmentsRef = useRef<StreamSegment[]>([]);
  const audioSegmentsRef = useRef<AudioStreamSegment[]>([]); // Dedicated audio segments
  const screenActiveRef = useRef<boolean>(false);
  const webcamActiveRef = useRef<boolean>(false);
  const audioActiveRef = useRef<boolean>(false); // Audio stream active state
  
  // Store video blobs for each segment (indexed by segmentIndex)
  const screenSegmentBlobsRef = useRef<Map<number, Blob>>(new Map());
  const webcamSegmentBlobsRef = useRef<Map<number, Blob>>(new Map());
  const audioSegmentBlobsRef = useRef<Map<number, Blob>>(new Map()); // Audio segment blobs
  
  // Store original streams for recreating recorders on toggle
  const originalScreenStreamRef = useRef<MediaStream | null>(null);
  const originalWebcamStreamRef = useRef<MediaStream | null>(null);
  const originalMicStreamRef = useRef<MediaStream | null>(null);
  const originalSystemAudioStreamRef = useRef<MediaStream | null>(null);
  const recordingQualityRef = useRef<RecordingQuality>('720p');
  
  // Audio stream state tracking
  const hasDedicatedAudioStreamRef = useRef<boolean>(true); // Always use dedicated audio stream
  const currentAudioHasMicRef = useRef<boolean>(false);
  const currentAudioHasSystemRef = useRef<boolean>(false);
  const mergedAudioStreamRef = useRef<MediaStream | null>(null); // Combined mic + system audio stream
  
  // Scene-based recording refs (null for blank mode)
  const sceneIdRef = useRef<string | null>(null);
  const projectIdRef = useRef<string | null>(null);
  
  // Webcam shape tracking for export/stitching
  const roundedWebcamRef = useRef<boolean>(false);
  const mirroredWebcamRef = useRef<boolean>(false);
  // Screen corner radius tracking (slight radius on screen share)
  const screenCornerRadiusRef = useRef<number>(12); // Default slight radius
  // Edge-to-edge tracking (no padding, no corner radius)
  const edgeToEdgeRef = useRef<boolean>(false);
  // Avatar tracking (static image instead of webcam video)
  const avatarWebcamRef = useRef<boolean>(false);
  const avatarUrlRef = useRef<string | null>(null);

  const getRecordingTime = useCallback((): number => {
    if (!recordingStartTimeRef.current) return 0;
    
    const now = Date.now();
    const elapsed = now - recordingStartTimeRef.current;
    const pausedDuration = totalPausedDurationRef.current + 
      (pauseStartTimeRef.current ? now - pauseStartTimeRef.current : 0);
    
    return Math.max(0, elapsed - pausedDuration);
  }, []);

  const createMediaRecorder = useCallback((
    stream: MediaStream,
    quality: RecordingQuality,
    onDataAvailable: (blob: Blob) => void
  ): MediaRecorder => {
    const mimeType = getSupportedMimeType(true);
    const videoBitrate = BITRATES_BY_QUALITY[quality];
    
    const options: MediaRecorderOptions = {
      videoBitsPerSecond: videoBitrate,
      audioBitsPerSecond: AUDIO_BITRATE,
    };
    
    if (mimeType) {
      options.mimeType = mimeType;
    }

    const recorder = new MediaRecorder(stream, options);
    
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        onDataAvailable(event.data);
      }
    };

    return recorder;
  }, []);

  const startRecording = useCallback(async (options: {
    screenStream: MediaStream | null;
    webcamStream: MediaStream | null;
    micStream: MediaStream | null;
    systemAudioStream: MediaStream | null;
    layout: BlankRecordingLayout;
    quality: RecordingQuality;
    sceneId?: string; // Optional scene ID for scene-based recording (null = blank mode)
    projectId?: string; // Optional project ID for scene-based recording
    roundedWebcam?: boolean; // Whether webcam is circular/rounded
    mirroredWebcam?: boolean; // Whether webcam is horizontally mirrored
    avatarWebcam?: boolean; // Whether using avatar image instead of webcam video
    avatarUrl?: string; // URL of avatar image (when avatarWebcam is true)
    edgeToEdge?: boolean; // Whether edge-to-edge mode is active (no padding, sharp corners)
  }) => {
    const isSceneMode = !!options.sceneId;
    
    // Store webcam shape for segment creation
    roundedWebcamRef.current = options.roundedWebcam || false;
    mirroredWebcamRef.current = options.mirroredWebcam || false;
    
    // Store edge-to-edge state - affects corner radius
    edgeToEdgeRef.current = options.edgeToEdge || false;
    if (edgeToEdgeRef.current) {
      screenCornerRadiusRef.current = 0;
    } else {
      screenCornerRadiusRef.current = 12;
    }
    
    // Store avatar state for segment creation
    avatarWebcamRef.current = options.avatarWebcam || false;
    avatarUrlRef.current = options.avatarUrl || null;
    
    // Store scene/project IDs
    sceneIdRef.current = options.sceneId || null;
    projectIdRef.current = options.projectId || null;
    
    if (!RECORDING_FLAGS.SEPARATE_STREAMS_MODE) {
      setState(prev => ({ ...prev, error: 'Separate streams mode is not enabled' }));
      return;
    }

    // Validate that at least one stream is provided (mic-only is allowed for scene mode with slides)
    if (!options.screenStream && !options.webcamStream && !options.micStream) {
      const error = 'At least one stream (screen, webcam, or mic) is required to start recording';
      logger.error('[SeparateStreamRecorder] ' + error);
      setState(prev => ({ ...prev, error }));
      return;
    }

    try {
      logger.info('[SeparateStreamRecorder] Starting separate stream recording', {
        hasScreenStream: !!options.screenStream,
        hasWebcamStream: !!options.webcamStream,
        hasMicStream: !!options.micStream,
        hasSystemAudio: !!options.systemAudioStream,
        mode: isSceneMode ? 'scene' : 'blank',
        sceneId: options.sceneId,
      });
      
      setState(prev => ({ ...prev, error: null }));
      
      // Different initialization for blank mode vs scene mode
      if (isSceneMode) {
        // Scene mode: use scene ID as recording ID, uploads go directly to S3
        recordingIdRef.current = options.sceneId!;
        uploadUrlsRef.current = null; // Will get presigned URLs when uploading
      } else {
        // Blank mode: use blankRecordingService
        const initiateResponse = await blankRecordingService.initiate(false);
        
        recordingIdRef.current = initiateResponse.recordingId;
        uploadUrlsRef.current = {
          screen: initiateResponse.uploadUrls.screen,
          webcam: initiateResponse.uploadUrls.webcam,
        };
        
        try {
          await blankSessionPersistence.startSession({
            recordingId: recordingIdRef.current!,
            layout: options.layout,
            micEnabled: !!options.micStream,
            hasDedicatedAudioStream: true,
            mirroredWebcam: options.mirroredWebcam || false,
            avatarWebcam: options.avatarWebcam || false,
            avatarUrl: options.avatarUrl || null,
            edgeToEdge: options.edgeToEdge || false,
            screenCornerRadius: screenCornerRadiusRef.current,
            quality: options.quality || 'standard',
          });
          blankSessionPersistence.setSegmentProvider(() => ({
            screen: screenSegmentsRef.current,
            webcam: webcamSegmentsRef.current,
            audio: audioSegmentsRef.current,
          }));
        } catch (err) {
          logger.warn('[SeparateStreamRecorder] Failed to start IDB session persistence (non-critical)', err);
        }
      }
      
      layoutRef.current = options.layout;
      micEnabledRef.current = !!options.micStream;
      
      logger.info('[SeparateStreamRecorder] Recording initialized', {
        recordingId: recordingIdRef.current,
        mode: isSceneMode ? 'scene' : 'blank',
      });

      screenChunksRef.current = [];
      webcamChunksRef.current = [];
      audioChunksRef.current = [];
      screenSegmentsRef.current = [];
      webcamSegmentsRef.current = [];
      audioSegmentsRef.current = [];
      audioSegmentBlobsRef.current.clear();
      
      // === DEDICATED AUDIO STREAM RECORDER ===
      // Collect all audio sources to create a single dedicated audio stream
      const audioSources: MediaStreamTrack[] = [];
      let hasMicAudio = false;
      let hasSystemAudio = false;
      
      // Get audio from screen share itself (if any)
      if (options.screenStream) {
        const screenAudioTracks = options.screenStream.getAudioTracks();
        audioSources.push(...screenAudioTracks);
        if (screenAudioTracks.length > 0) hasSystemAudio = true;
      }
      
      // Add system audio (from screen share - may be separate stream)
      if (options.systemAudioStream) {
        const systemAudioTracks = options.systemAudioStream.getAudioTracks();
        audioSources.push(...systemAudioTracks);
        if (systemAudioTracks.length > 0) hasSystemAudio = true;
      }
      
      // Add mic audio
      if (options.micStream) {
        const micAudioTracks = options.micStream.getAudioTracks();
        audioSources.push(...micAudioTracks);
        if (micAudioTracks.length > 0) hasMicAudio = true;
      }
      
      
      // Create dedicated audio recorder if there are any audio sources
      if (audioSources.length > 0) {
        // Use Web Audio API to merge multiple audio tracks into one
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        
        const destination = audioContext.createMediaStreamDestination();
        
        // Connect each audio source to the destination
        audioSources.forEach((track, index) => {
          const tempStream = new MediaStream([track]);
          const source = audioContext.createMediaStreamSource(tempStream);
          source.connect(destination);
        });
        
        // Get the merged audio stream
        const mergedAudioStream = destination.stream;
        mergedAudioStreamRef.current = mergedAudioStream;
        
        // Create audio-only MediaRecorder
        const audioMimeType = 'audio/webm;codecs=opus';
        const audioRecorder = new MediaRecorder(mergedAudioStream, {
          mimeType: audioMimeType,
          audioBitsPerSecond: AUDIO_BITRATE,
        });
        
        audioRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
            if (!sceneIdRef.current) {
              const currentSegment = audioSegmentsRef.current[audioSegmentsRef.current.length - 1];
              blankSessionPersistence.bufferChunk('audio', currentSegment?.segmentIndex ?? 0, event.data);
            }
          }
        };
        
        audioRecorderRef.current = audioRecorder;
        audioRecorder.start(SEPARATE_STREAM_TIMESLICE);
        audioActiveRef.current = true;
        
        // Track audio state
        currentAudioHasMicRef.current = hasMicAudio;
        currentAudioHasSystemRef.current = hasSystemAudio;
        
        // Initialize first audio segment
        audioSegmentsRef.current.push({
          segmentIndex: 0,
          tsStart: 0,
          tsEnd: null,
          uploadStatus: 'pending',
          hasMic: hasMicAudio,
          hasSystemAudio: hasSystemAudio,
          isMuted: false,
        });
        
        logger.info('[SeparateStreamRecorder] Dedicated audio recorder started with initial segment', {
          hasMic: hasMicAudio,
          hasSystemAudio: hasSystemAudio,
        });
      } else {
        // No audio sources - mark as muted
        hasDedicatedAudioStreamRef.current = true;
        audioActiveRef.current = false;
      }
      
      // === VIDEO-ONLY SCREEN RECORDER ===
      if (options.screenStream) {
        
        // Get video track from screen stream - NO AUDIO
        const videoTrack = options.screenStream.getVideoTracks()[0];
        if (!videoTrack) {
          throw new Error('No video track in screen stream');
        }
        
        // Create video-only stream (no audio - audio is in dedicated stream)
        const screenStreamVideoOnly = new MediaStream([videoTrack]);
        
        // Mark screen as having no audio (audio is in dedicated stream)
        screenHasAudioRef.current = false;
        
        const screenRecorder = createMediaRecorder(
          screenStreamVideoOnly,
          options.quality,
          (blob) => {
            screenChunksRef.current.push(blob);
            if (!sceneIdRef.current) {
              const currentSegment = screenSegmentsRef.current[screenSegmentsRef.current.length - 1];
              blankSessionPersistence.bufferChunk('screen', currentSegment?.segmentIndex ?? 0, blob);
            }
          }
        );
        
        screenRecorderRef.current = screenRecorder;
        screenRecorder.start(SEPARATE_STREAM_TIMESLICE);
        screenActiveRef.current = true;
        // Initialize first segment for screen (starts at 0) with initial position and corner radius
        const screenLayout = options.layout.screen;
        screenSegmentsRef.current.push({
          segmentIndex: 0,
          tsStart: 0,
          tsEnd: null,
          hasAudio: false, // Audio is in dedicated audio stream
          uploadStatus: 'pending',
          x: screenLayout?.x,
          y: screenLayout?.y,
          width: screenLayout?.width,
          height: screenLayout?.height,
          isCircle: false, // Screen is always rectangular
          cornerRadius: screenCornerRadiusRef.current,
        });
        // Store original stream for toggle recreation
        originalScreenStreamRef.current = options.screenStream;
        logger.info('[SeparateStreamRecorder] Screen recorder started with initial segment');
      }
      
      if (options.webcamStream) {
        
        // Get only video track - NO AUDIO (audio is in dedicated stream)
        const videoTrack = options.webcamStream.getVideoTracks()[0];
        if (!videoTrack) {
          throw new Error('No video track in webcam stream');
        }
        
        const webcamStreamVideoOnly = new MediaStream([videoTrack]);
        
        // Mark webcam as having no audio (audio is in dedicated stream)
        webcamHasAudioRef.current = false;
        
        const webcamRecorder = createMediaRecorder(
          webcamStreamVideoOnly,
          options.quality,
          (blob) => {
            webcamChunksRef.current.push(blob);
            if (!sceneIdRef.current) {
              const currentSegment = webcamSegmentsRef.current[webcamSegmentsRef.current.length - 1];
              blankSessionPersistence.bufferChunk('webcam', currentSegment?.segmentIndex ?? 0, blob);
            }
          }
        );
        
        webcamRecorderRef.current = webcamRecorder;
        webcamRecorder.start(SEPARATE_STREAM_TIMESLICE);
        webcamActiveRef.current = true;
        // Initialize first segment for webcam (starts at 0) with initial position, shape, and avatar
        // Note: hasAudio is always false - audio is in dedicated stream
        const webcamLayout = options.layout.webcam;
        const isRounded = roundedWebcamRef.current;
        const isAvatar = avatarWebcamRef.current;
        const currentAvatarUrl = avatarUrlRef.current;
        webcamSegmentsRef.current.push({
          segmentIndex: 0,
          tsStart: 0,
          tsEnd: null,
          hasAudio: false, // Audio is in dedicated audio stream
          uploadStatus: 'pending',
          x: webcamLayout?.x,
          y: webcamLayout?.y,
          width: webcamLayout?.width,
          height: webcamLayout?.height,
          isCircle: isRounded,
          cornerRadius: edgeToEdgeRef.current ? 0 : (isRounded ? Math.min(webcamLayout?.width || 0, webcamLayout?.height || 0) / 2 : undefined),
          isMirrored: mirroredWebcamRef.current,
          // Avatar properties
          isAvatar: isAvatar,
          avatarUrl: isAvatar ? currentAvatarUrl || undefined : undefined,
        });
        // Store original stream for toggle recreation
        originalWebcamStreamRef.current = options.webcamStream;
        logger.info('[SeparateStreamRecorder] Webcam recorder started with initial segment');
      }

      // Store mic and system audio streams for toggle recreation
      originalMicStreamRef.current = options.micStream;
      originalSystemAudioStreamRef.current = options.systemAudioStream;
      recordingQualityRef.current = options.quality;

      recordingStartTimeRef.current = Date.now();
      totalPausedDurationRef.current = 0;
      pauseStartTimeRef.current = null;
      
      setState({
        isRecording: true,
        streamActive: {
          screen: !!options.screenStream,
          webcam: !!options.webcamStream,
          audio: audioActiveRef.current,
        },
        isPaused: false,
        recordingId: recordingIdRef.current,
        recordingTime: 0,
        uploadProgress: { screen: null, webcam: null, audio: null },
        error: null,
        pendingRecording: null,
      });
      
      logger.info('[SeparateStreamRecorder] Recording started successfully');
      
    } catch (error: any) {
      logger.error('[SeparateStreamRecorder] Failed to start recording', error);
      setState(prev => ({ 
        ...prev, 
        error: error.message || 'Failed to start recording' 
      }));
    }
  }, [createMediaRecorder]);

  const stopRecording = useCallback(async () => {
    logger.info('[SeparateStreamRecorder] Stopping recording');
    
    const duration = getRecordingTime();
    
    const stopPromises: Promise<void>[] = [];
    
    const assembleSegmentBlob = (
      chunksRef: React.MutableRefObject<Blob[]>,
      segmentsRef: React.MutableRefObject<any[]>,
      blobsRef: React.MutableRefObject<Map<number, Blob>>,
      mimeType: string,
    ) => {
      const lastSegment = segmentsRef.current[segmentsRef.current.length - 1];
      if (lastSegment && chunksRef.current.length > 0) {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        blobsRef.current.set(lastSegment.segmentIndex, blob);
        if (lastSegment.tsEnd === null) {
          lastSegment.tsEnd = duration;
        }
      }
    };
    
    const videoMimeType = getSupportedMimeType(true) || 'video/webm';
    const audioMimeType = 'audio/webm;codecs=opus';
    
    if (screenRecorderRef.current && screenRecorderRef.current.state !== 'inactive') {
      stopPromises.push(new Promise<void>((resolve) => {
        screenRecorderRef.current!.onstop = () => {
          assembleSegmentBlob(screenChunksRef, screenSegmentsRef, screenSegmentBlobsRef, videoMimeType);
          resolve();
        };
        screenRecorderRef.current!.stop();
      }));
    } else if (screenChunksRef.current.length > 0) {
      logger.info('[SeparateStreamRecorder] Screen recorder already inactive, assembling blob from existing chunks');
      assembleSegmentBlob(screenChunksRef, screenSegmentsRef, screenSegmentBlobsRef, videoMimeType);
    }
    
    if (webcamRecorderRef.current && webcamRecorderRef.current.state !== 'inactive') {
      stopPromises.push(new Promise<void>((resolve) => {
        webcamRecorderRef.current!.onstop = () => {
          assembleSegmentBlob(webcamChunksRef, webcamSegmentsRef, webcamSegmentBlobsRef, videoMimeType);
          resolve();
        };
        webcamRecorderRef.current!.stop();
      }));
    } else if (webcamChunksRef.current.length > 0) {
      logger.info('[SeparateStreamRecorder] Webcam recorder already inactive, assembling blob from existing chunks');
      assembleSegmentBlob(webcamChunksRef, webcamSegmentsRef, webcamSegmentBlobsRef, videoMimeType);
    }
    
    if (audioRecorderRef.current && audioRecorderRef.current.state !== 'inactive') {
      stopPromises.push(new Promise<void>((resolve) => {
        audioRecorderRef.current!.onstop = () => {
          assembleSegmentBlob(audioChunksRef, audioSegmentsRef, audioSegmentBlobsRef, audioMimeType);
          resolve();
        };
        audioRecorderRef.current!.stop();
      }));
    } else if (audioChunksRef.current.length > 0) {
      logger.info('[SeparateStreamRecorder] Audio recorder already inactive, assembling blob from existing chunks');
      assembleSegmentBlob(audioChunksRef, audioSegmentsRef, audioSegmentBlobsRef, audioMimeType);
    }
    
    await Promise.all(stopPromises);
    
    // Close any open segments that might have been left open (e.g., if stream was toggled off before stop)
    for (const segment of screenSegmentsRef.current) {
      if (segment.tsEnd === null) {
        segment.tsEnd = duration;
      }
    }
    for (const segment of webcamSegmentsRef.current) {
      if (segment.tsEnd === null) {
        segment.tsEnd = duration;
      }
    }
    for (const segment of audioSegmentsRef.current) {
      if (segment.tsEnd === null) {
        segment.tsEnd = duration;
      }
    }
    
    if (!sceneIdRef.current) {
      try {
        await blankSessionPersistence.stopSession(
          screenSegmentsRef.current.map(s => ({ ...s })),
          webcamSegmentsRef.current.map(s => ({ ...s })),
          audioSegmentsRef.current.map(s => ({ ...s }))
        );
      } catch (err) {
        logger.warn('[SeparateStreamRecorder] Failed to persist final session state (non-critical)', err);
      }
    }
    
    // Update layout with final segments
    if (layoutRef.current) {
      // Create screen layout object if we have screen segments but no screen layout
      if (screenSegmentsRef.current.length > 0) {
        if (!layoutRef.current.screen) {
          const firstSeg = screenSegmentsRef.current[0];
          layoutRef.current.screen = {
            x: firstSeg.x ?? 16,
            y: firstSeg.y ?? 138,
            width: firstSeg.width ?? 924,
            height: firstSeg.height ?? 443,
            visible: true,
            segments: [],
          };
        }
        layoutRef.current.screen.segments = [...screenSegmentsRef.current];
        (layoutRef.current.screen as any).cornerRadius = screenCornerRadiusRef.current;
      }
      
      // Create webcam layout object if we have webcam segments but no webcam layout
      if (webcamSegmentsRef.current.length > 0) {
        if (!layoutRef.current.webcam) {
          const firstSeg = webcamSegmentsRef.current[0];
          layoutRef.current.webcam = {
            x: firstSeg.x ?? 956,
            y: firstSeg.y ?? 138,
            width: firstSeg.width ?? 308,
            height: firstSeg.height ?? 344,
            visible: true,
            segments: [],
          };
        }
        layoutRef.current.webcam.segments = [...webcamSegmentsRef.current];
        (layoutRef.current.webcam as any).mirrored = mirroredWebcamRef.current;
        (layoutRef.current.webcam as any).isAvatar = avatarWebcamRef.current;
        if (avatarWebcamRef.current && avatarUrlRef.current) {
          (layoutRef.current.webcam as any).avatarUrl = avatarUrlRef.current;
        }
        const isRounded = roundedWebcamRef.current;
        const wcWidth = layoutRef.current.webcam.width ?? 308;
        const wcHeight = layoutRef.current.webcam.height ?? 344;
        (layoutRef.current.webcam as any).isCircle = isRounded;
        (layoutRef.current.webcam as any).cornerRadius = edgeToEdgeRef.current ? 0 : (isRounded ? Math.min(wcWidth, wcHeight) / 2 : 0);
        (layoutRef.current.webcam as any).shape = isRounded ? 'rounded' : null;
      }
    }
    
    
    // Determine mode based on sceneId
    const isActualSceneMode = sceneIdRef.current !== null;
    
    // For scene mode: defer upload, create blob URLs for preview
    if (isActualSceneMode) {
      logger.info('[SeparateStreamRecorder] Scene mode - deferring upload for preview');
      
      // Create blob URLs for preview
      let screenBlobUrl: string | null = null;
      let webcamBlobUrl: string | null = null;
      
      // Get first screen segment blob URL - use actual first segment's index, not hardcoded 0
      const firstScreenSegment = screenSegmentsRef.current[0];
      if (firstScreenSegment) {
        const firstScreenBlob = screenSegmentBlobsRef.current.get(firstScreenSegment.segmentIndex);
        if (firstScreenBlob) {
          screenBlobUrl = URL.createObjectURL(firstScreenBlob);
        }
      }
      
      // Get first webcam segment blob URL - use actual first segment's index
      const firstWebcamSegment = webcamSegmentsRef.current[0];
      if (firstWebcamSegment) {
        const firstWebcamBlob = webcamSegmentBlobsRef.current.get(firstWebcamSegment.segmentIndex);
        if (firstWebcamBlob) {
          webcamBlobUrl = URL.createObjectURL(firstWebcamBlob);
        }
      }
      
      // Use primary stream for combined preview (screen takes precedence)
      const combinedBlobUrl = screenBlobUrl || webcamBlobUrl;
      
      // Create per-segment blob URLs for compositor playback
      const screenSegmentBlobUrls: SegmentBlobUrl[] = [];
      screenSegmentsRef.current.forEach(seg => {
        const blob = screenSegmentBlobsRef.current.get(seg.segmentIndex);
        if (blob) {
          screenSegmentBlobUrls.push({
            segmentIndex: seg.segmentIndex,
            blobUrl: URL.createObjectURL(blob),
          });
        }
      });
      
      const webcamSegmentBlobUrls: SegmentBlobUrl[] = [];
      webcamSegmentsRef.current.forEach(seg => {
        const blob = webcamSegmentBlobsRef.current.get(seg.segmentIndex);
        if (blob) {
          webcamSegmentBlobUrls.push({
            segmentIndex: seg.segmentIndex,
            blobUrl: URL.createObjectURL(blob),
          });
        }
      });
      
      // Create per-segment audio blob URLs for dedicated audio stream
      const audioSegmentBlobUrls: SegmentBlobUrl[] = [];
      let audioBlobUrl: string | null = null;
      audioSegmentsRef.current.forEach(seg => {
        const blob = audioSegmentBlobsRef.current.get(seg.segmentIndex);
        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          audioSegmentBlobUrls.push({
            segmentIndex: seg.segmentIndex,
            blobUrl,
          });
          // Use first audio segment for main audio blob URL
          if (!audioBlobUrl) {
            audioBlobUrl = blobUrl;
          }
        }
      });
      
      
      const pendingRecording: PendingRecording = {
        screenBlobUrl,
        webcamBlobUrl,
        audioBlobUrl,
        combinedBlobUrl,
        duration,
        micEnabled: micEnabledRef.current,
        hasDedicatedAudioStream: hasDedicatedAudioStreamRef.current,
        layout: layoutRef.current!,
        sceneId: sceneIdRef.current,
        screenSegmentBlobUrls,
        webcamSegmentBlobUrls,
        audioSegmentBlobUrls,
      };
      
      setState(prev => ({ 
        ...prev, 
        isRecording: false,
        pendingRecording,
      }));
      
      logger.info('[SeparateStreamRecorder] Pending recording created for scene preview', {
        sceneId: sceneIdRef.current,
        hasScreen: !!screenBlobUrl,
        hasWebcam: !!webcamBlobUrl,
        duration,
      });
      
      // Persist to IndexedDB for recovery if user navigates away
      try {
        // First, delete ALL existing pending recordings for this sceneId to avoid stale data
        await deleteAllPendingRecordingsForScene(sceneIdRef.current!);
        
        const screenSegmentsForStorage = screenSegmentsRef.current.map((seg) => {
          const blob = screenSegmentBlobsRef.current.get(seg.segmentIndex);
          return {
            segmentIndex: seg.segmentIndex,
            blob: blob!,
            tsStart: seg.tsStart,
            tsEnd: seg.tsEnd,
            hasAudio: seg.hasAudio,
            x: seg.x ?? 0,
            y: seg.y ?? 0,
            width: seg.width ?? 0,
            height: seg.height ?? 0,
          };
        }).filter(s => s.blob);
        
        const webcamSegmentsForStorage = webcamSegmentsRef.current.map((seg) => {
          const blob = webcamSegmentBlobsRef.current.get(seg.segmentIndex);
          return {
            segmentIndex: seg.segmentIndex,
            blob: blob!,
            tsStart: seg.tsStart,
            tsEnd: seg.tsEnd,
            hasAudio: seg.hasAudio,
            x: seg.x ?? 0,
            y: seg.y ?? 0,
            width: seg.width ?? 0,
            height: seg.height ?? 0,
          };
        }).filter(s => s.blob);
        
        // Persist audio segments for recovery (dedicated audio stream)
        const audioSegmentsForStorage = audioSegmentsRef.current.map((seg) => {
          const blob = audioSegmentBlobsRef.current.get(seg.segmentIndex);
          return {
            segmentIndex: seg.segmentIndex,
            blob: blob!,
            tsStart: seg.tsStart,
            tsEnd: seg.tsEnd,
            hasMic: seg.hasMic,
            hasSystemAudio: seg.hasSystemAudio,
            isMuted: seg.isMuted,
          };
        }).filter(s => s.blob);
        
        const pendingRecordingRow: IPendingRecordingRow = {
          id: `pending-${sceneIdRef.current}-${Date.now()}`,
          sceneId: sceneIdRef.current!,
          projectId: projectIdRef.current || undefined,
          screenSegments: screenSegmentsForStorage,
          webcamSegments: webcamSegmentsForStorage,
          audioSegments: audioSegmentsForStorage.length > 0 ? audioSegmentsForStorage : undefined,
          layout: layoutRef.current,
          duration,
          micEnabled: micEnabledRef.current,
          createdAt: Date.now(),
        };
        
        await savePendingRecording(pendingRecordingRow);
        logger.info('[SeparateStreamRecorder] Pending recording saved to IndexedDB for recovery');
      } catch (idbError) {
        logger.warn('[SeparateStreamRecorder] Failed to save pending recording to IndexedDB (non-critical)', idbError);
      }
      
      // Return pending recording - don't upload or clear refs yet
      return pendingRecording;
    }
    
    // Blank mode: immediate upload
    logger.info('[SeparateStreamRecorder] Blank mode - uploading segments immediately');
    setState(prev => ({ ...prev, isRecording: false }));
    callbacks?.onUploadStart?.();
    
    try {
      // Upload ALL segment blobs (may have multiple per stream due to toggling)
      const uploadedScreenSegments: StreamSegment[] = [];
      const uploadedWebcamSegments: StreamSegment[] = [];
      
      // Upload screen segments (blank mode only - scene mode returns early)
      for (const segment of screenSegmentsRef.current) {
        const blob = screenSegmentBlobsRef.current.get(segment.segmentIndex);
        if (blob) {
          
          const { url, key: uploadKey } = await blankRecordingService.getSegmentUploadUrl(
            recordingIdRef.current!,
            'screen',
            segment.segmentIndex
          );
          
          const response = await fetch(url, {
            method: 'PUT',
            body: blob,
            headers: {
              'Content-Type': 'video/webm',
            },
          });
          
          if (!response.ok) {
            throw new Error(`Failed to upload screen segment ${segment.segmentIndex}: ${response.status} ${response.statusText}`);
          }
          
          // Update segment with uploaded key
          segment.key = uploadKey;
          segment.uploadStatus = 'completed';
          uploadedScreenSegments.push(segment);
        }
      }
      
      // Upload webcam segments (blank mode only - scene mode returns early)
      for (const segment of webcamSegmentsRef.current) {
        const blob = webcamSegmentBlobsRef.current.get(segment.segmentIndex);
        if (blob) {
          
          const { url, key: uploadKey } = await blankRecordingService.getSegmentUploadUrl(
            recordingIdRef.current!,
            'webcam',
            segment.segmentIndex
          );
          
          const response = await fetch(url, {
            method: 'PUT',
            body: blob,
            headers: {
              'Content-Type': 'video/webm',
            },
          });
          
          if (!response.ok) {
            throw new Error(`Failed to upload webcam segment ${segment.segmentIndex}: ${response.status} ${response.statusText}`);
          }
          
          // Update segment with uploaded key
          segment.key = uploadKey;
          segment.uploadStatus = 'completed';
          uploadedWebcamSegments.push(segment);
        }
      }
      
      // Upload audio segments if we have dedicated audio stream
      const uploadedAudioSegments: AudioStreamSegment[] = [];
      if (hasDedicatedAudioStreamRef.current && audioSegmentsRef.current.length > 0) {
        for (const segment of audioSegmentsRef.current) {
          const blob = audioSegmentBlobsRef.current.get(segment.segmentIndex);
          if (blob) {
            
            const { url, key: uploadKey } = await blankRecordingService.getAudioSegmentUploadUrl(
              recordingIdRef.current!,
              segment.segmentIndex
            );
            
            const response = await fetch(url, {
              method: 'PUT',
              body: blob,
              headers: {
                'Content-Type': 'audio/webm',
              },
            });
            
            if (!response.ok) {
              throw new Error(`Failed to upload audio segment ${segment.segmentIndex}: ${response.status} ${response.statusText}`);
            }
            
            // Update segment with uploaded key
            segment.key = uploadKey;
            segment.uploadStatus = 'completed';
            uploadedAudioSegments.push(segment);
          }
        }
      }
      
      // Upload avatar images to S3 for any avatar webcam segments
      if (uploadedWebcamSegments.some(s => s.isAvatar && s.avatarUrl)) {
        const pid = projectIdRef.current || recordingIdRef.current || '0';
        await uploadAvatarSegmentsToS3(uploadedWebcamSegments, pid);
      }

      // Complete recording (blank mode only - scene mode returns early)
      if (recordingIdRef.current && layoutRef.current) {
        const hasMultipleScreenSegments = uploadedScreenSegments.length > 1;
        const hasMultipleWebcamSegments = uploadedWebcamSegments.length > 1;
        
        // Derive duration from actual segment end times, not wall-clock timer
        // (wall-clock includes upload time which inflates the value)
        const allSegmentEnds = [
          ...uploadedScreenSegments.map(s => s.tsEnd ?? 0),
          ...uploadedWebcamSegments.map(s => s.tsEnd ?? 0),
          ...uploadedAudioSegments.map(s => s.tsEnd ?? 0),
        ];
        const duration = allSegmentEnds.length > 0
          ? Math.max(...allSegmentEnds)
          : getRecordingTime();
        
        const completeRequest: any = {
          recordingId: recordingIdRef.current,
          streams: {},
          layout: layoutRef.current,
          duration,
          micEnabled: micEnabledRef.current,
          hasDedicatedAudioStream: hasDedicatedAudioStreamRef.current,
        };
        
        // For screen stream
        if (uploadedScreenSegments.length > 0) {
          if (hasMultipleScreenSegments) {
            completeRequest.streams.screen = { segments: uploadedScreenSegments };
          } else {
            // Single segment - include all segment properties for recomposition
            const seg = uploadedScreenSegments[0];
            completeRequest.streams.screen = {
              key: seg.key!,
              hasAudio: seg.hasAudio,
              // Include position and shape for compositor
              x: seg.x,
              y: seg.y,
              width: seg.width,
              height: seg.height,
              isCircle: seg.isCircle,
              cornerRadius: seg.cornerRadius,
            };
          }
        }
        
        // For webcam stream
        if (uploadedWebcamSegments.length > 0) {
          if (hasMultipleWebcamSegments) {
            completeRequest.streams.webcam = { segments: uploadedWebcamSegments };
          } else {
            // Single segment - include all segment properties for recomposition
            const seg = uploadedWebcamSegments[0];
            completeRequest.streams.webcam = {
              key: seg.key!,
              hasAudio: seg.hasAudio,
              // Include position and shape for compositor
              x: seg.x,
              y: seg.y,
              width: seg.width,
              height: seg.height,
              isCircle: seg.isCircle,
              cornerRadius: seg.cornerRadius,
              // Include avatar properties for compositor
              isAvatar: seg.isAvatar,
              avatarUrl: seg.avatarUrl,
            };
          }
        }
        
        // For audio stream (dedicated audio separate from video)
        if (uploadedAudioSegments.length > 0) {
          completeRequest.streams.audio = { segments: uploadedAudioSegments };
        }
        
        
        const completeResponse = await blankRecordingService.complete(completeRequest);
        
        logger.info('[SeparateStreamRecorder] Recording completed on backend', {
          recordingId: completeResponse.recordingId,
          databaseId: completeResponse.databaseId,
        });
        
        try {
          await blankSessionPersistence.endSession();
        } catch (err) {
          logger.warn('[SeparateStreamRecorder] Failed to clean up IDB session (non-critical)', err);
        }
        
        // Notify upload complete
        callbacks?.onUploadComplete?.(completeResponse.recordingId);
      }
      
    } catch (error: any) {
      logger.error('[SeparateStreamRecorder] Upload/complete failed', error);
      const errorMsg = error.message || 'Failed to upload recording';
      setState(prev => ({ 
        ...prev, 
        error: errorMsg
      }));
      // Notify upload error
      callbacks?.onUploadError?.(errorMsg);
    }
    
    // Cleanup refs
    screenRecorderRef.current = null;
    webcamRecorderRef.current = null;
    screenChunksRef.current = [];
    webcamChunksRef.current = [];
    screenSegmentsRef.current = [];
    webcamSegmentsRef.current = [];
    screenSegmentBlobsRef.current.clear();
    webcamSegmentBlobsRef.current.clear();
    screenActiveRef.current = false;
    webcamActiveRef.current = false;
    originalScreenStreamRef.current = null;
    originalWebcamStreamRef.current = null;
    originalMicStreamRef.current = null;
    originalSystemAudioStreamRef.current = null;
    
    // Cleanup AudioContext
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    
    return null; // Blank mode returns null (no pending recording)
  }, [getRecordingTime, callbacks]);

  // Upload pending recording (scene mode - called when user confirms in preview modal)
  const uploadRecording = useCallback(async (): Promise<any> => {
    const pending = state.pendingRecording;
    if (!pending || !pending.sceneId) {
      logger.warn('[SeparateStreamRecorder] uploadRecording called but no pending recording');
      return;
    }
    
    logger.info('[SeparateStreamRecorder] Uploading pending recording for scene', { sceneId: pending.sceneId });
    
    try {
      callbacks?.onUploadStart?.();
      
      const uploadedScreenSegments: StreamSegment[] = [];
      const uploadedWebcamSegments: StreamSegment[] = [];
      const uploadedAudioSegments: AudioStreamSegment[] = [];
      
      // Upload screen segments
      for (const segment of screenSegmentsRef.current) {
        const blob = screenSegmentBlobsRef.current.get(segment.segmentIndex);
        if (blob) {
          const fileName = `scene_${pending.sceneId}_screen_${segment.segmentIndex}_${Date.now()}.webm`;
          const fileUrl = await projectsManager.uploadVideoToS3(blob, fileName);
          
          segment.key = fileUrl;
          segment.uploadStatus = 'completed';
          uploadedScreenSegments.push(segment);
        }
      }
      
      // Upload webcam segments
      for (const segment of webcamSegmentsRef.current) {
        const blob = webcamSegmentBlobsRef.current.get(segment.segmentIndex);
        if (blob) {
          const fileName = `scene_${pending.sceneId}_webcam_${segment.segmentIndex}_${Date.now()}.webm`;
          const fileUrl = await projectsManager.uploadVideoToS3(blob, fileName);
          
          segment.key = fileUrl;
          segment.uploadStatus = 'completed';
          uploadedWebcamSegments.push(segment);
        }
      }
      
      // Upload audio segments (dedicated audio stream)
      if (hasDedicatedAudioStreamRef.current && audioSegmentsRef.current.length > 0) {
        for (const segment of audioSegmentsRef.current) {
          const blob = audioSegmentBlobsRef.current.get(segment.segmentIndex);
          if (blob) {
            const fileName = `scene_${pending.sceneId}_audio_${segment.segmentIndex}_${Date.now()}.webm`;
            const fileUrl = await projectsManager.uploadVideoToS3(blob, fileName);
            
            segment.key = fileUrl;
            segment.uploadStatus = 'completed';
            uploadedAudioSegments.push(segment);
          }
        }
      }
      
      // Upload avatar images to S3 for any avatar webcam segments (scene mode)
      if (uploadedWebcamSegments.some(s => s.isAvatar && s.avatarUrl)) {
        const pid = projectIdRef.current || pending.sceneId || '0';
        await uploadAvatarSegmentsToS3(uploadedWebcamSegments, pid);
      }

      // Create separate stream metadata
      const separateStreamMetadata = {
        recordingType: 'separate_streams' as const,
        layout: pending.layout,
        duration: pending.duration,
        micEnabled: pending.micEnabled,
        streams: {
          screen: uploadedScreenSegments.length > 0 ? {
            segments: uploadedScreenSegments,
          } : undefined,
          webcam: uploadedWebcamSegments.length > 0 ? {
            segments: uploadedWebcamSegments,
          } : undefined,
          audio: uploadedAudioSegments.length > 0 ? {
            segments: uploadedAudioSegments,
          } : undefined,
        },
        capturedAt: new Date().toISOString(),
        version: '2.1',
      };
      
      // Update scene metadata with separate stream data
      const sceneId = parseInt(pending.sceneId, 10);
      if (!isNaN(sceneId)) {
        await projectsManager.updateScene(sceneId, {
          status: 'recorded',
          duration: pending.duration,
          micEnabled: pending.micEnabled,
          metadata: separateStreamMetadata,
          videoUrl: uploadedScreenSegments[0]?.key || uploadedWebcamSegments[0]?.key || undefined,
          // Do NOT reset analysisStatus here - preserve existing analysis
          // Analysis is only reset when actually retaking (handleReRecordScene)
        });
        
        logger.info('[SeparateStreamRecorder] Scene recording uploaded', {
          sceneId,
          screenSegments: uploadedScreenSegments.length,
          webcamSegments: uploadedWebcamSegments.length,
          audioSegments: uploadedAudioSegments.length,
        });
        
        callbacks?.onUploadComplete?.(pending.sceneId);
        
        // Cleanup after successful upload
        clearPendingRecording();
        
        // Return metadata for local state update
        return {
          metadata: separateStreamMetadata,
          videoUrl: uploadedScreenSegments[0]?.key || uploadedWebcamSegments[0]?.key || undefined,
        };
      }
      
      // Cleanup after successful upload (fallback)
      clearPendingRecording();
      return null;
      
    } catch (error: any) {
      logger.error('[SeparateStreamRecorder] Scene upload failed', error);
      const errorMsg = error.message || 'Failed to upload scene recording';
      setState(prev => ({ ...prev, error: errorMsg }));
      callbacks?.onUploadError?.(errorMsg);
    }
  }, [state.pendingRecording, callbacks]);

  // Upload from recovered IndexedDB data (for persistence after navigation)
  const uploadFromRecoveredData = useCallback(async (recoveredData: any): Promise<void> => {
    if (!recoveredData || !recoveredData.sceneId) {
      logger.warn('[SeparateStreamRecorder] uploadFromRecoveredData called but no recovered data');
      return;
    }
    
    logger.info('[SeparateStreamRecorder] Uploading recovered recording for scene', { 
      sceneId: recoveredData.sceneId,
      screenSegments: recoveredData.screenSegments?.length || 0,
      webcamSegments: recoveredData.webcamSegments?.length || 0,
      audioSegments: recoveredData.audioSegments?.length || 0,
    });
    
    try {
      callbacks?.onUploadStart?.();
      
      const uploadedScreenSegments: StreamSegment[] = [];
      const uploadedWebcamSegments: StreamSegment[] = [];
      const uploadedAudioSegments: AudioStreamSegment[] = [];
      
      // Upload screen segments from recovered data
      if (recoveredData.screenSegments) {
        for (const segment of recoveredData.screenSegments) {
          if (segment.blob) {
            const fileName = `scene_${recoveredData.sceneId}_screen_${segment.segmentIndex}_${Date.now()}.webm`;
            const fileUrl = await projectsManager.uploadVideoToS3(segment.blob, fileName);
            
            const uploadedSegment: StreamSegment = {
              segmentIndex: segment.segmentIndex,
              tsStart: segment.tsStart,
              tsEnd: segment.tsEnd,
              hasAudio: segment.hasAudio,
              key: fileUrl,
              uploadStatus: 'completed',
              // Include position and shape from recovered segment
              x: segment.x,
              y: segment.y,
              width: segment.width,
              height: segment.height,
              isCircle: segment.isCircle,
              cornerRadius: segment.cornerRadius,
            };
            uploadedScreenSegments.push(uploadedSegment);
          }
        }
      }
      
      // Upload webcam segments from recovered data
      if (recoveredData.webcamSegments) {
        for (const segment of recoveredData.webcamSegments) {
          if (segment.blob) {
            const fileName = `scene_${recoveredData.sceneId}_webcam_${segment.segmentIndex}_${Date.now()}.webm`;
            const fileUrl = await projectsManager.uploadVideoToS3(segment.blob, fileName);
            
            const uploadedSegment: StreamSegment = {
              segmentIndex: segment.segmentIndex,
              tsStart: segment.tsStart,
              tsEnd: segment.tsEnd,
              hasAudio: segment.hasAudio,
              key: fileUrl,
              uploadStatus: 'completed',
              // Include position and shape from recovered segment
              x: segment.x,
              y: segment.y,
              width: segment.width,
              height: segment.height,
              isCircle: segment.isCircle,
              cornerRadius: segment.cornerRadius,
              // Include avatar properties from recovered segment
              isAvatar: segment.isAvatar,
              avatarUrl: segment.avatarUrl,
            };
            uploadedWebcamSegments.push(uploadedSegment);
          }
        }
      }
      
      // Upload audio segments from recovered data
      if (recoveredData.audioSegments) {
        for (const segment of recoveredData.audioSegments) {
          if (segment.blob) {
            const fileName = `scene_${recoveredData.sceneId}_audio_${segment.segmentIndex}_${Date.now()}.webm`;
            const fileUrl = await projectsManager.uploadVideoToS3(segment.blob, fileName);
            
            const uploadedSegment: AudioStreamSegment = {
              segmentIndex: segment.segmentIndex,
              tsStart: segment.tsStart,
              tsEnd: segment.tsEnd,
              hasMic: segment.hasMic,
              hasSystemAudio: segment.hasSystemAudio,
              isMuted: segment.isMuted,
              key: fileUrl,
              uploadStatus: 'completed',
            };
            uploadedAudioSegments.push(uploadedSegment);
          }
        }
      }
      
      // Upload avatar images to S3 for any avatar webcam segments (recovered data)
      if (uploadedWebcamSegments.some(s => s.isAvatar && s.avatarUrl)) {
        const pid = recoveredData.projectId || recoveredData.sceneId || '0';
        await uploadAvatarSegmentsToS3(uploadedWebcamSegments, pid);
      }

      // Create separate stream metadata
      const separateStreamMetadata = {
        recordingType: 'separate_streams' as const,
        layout: recoveredData.layout,
        duration: recoveredData.duration,
        micEnabled: recoveredData.micEnabled,
        streams: {
          screen: uploadedScreenSegments.length > 0 ? {
            segments: uploadedScreenSegments,
          } : undefined,
          webcam: uploadedWebcamSegments.length > 0 ? {
            segments: uploadedWebcamSegments,
          } : undefined,
          audio: uploadedAudioSegments.length > 0 ? {
            segments: uploadedAudioSegments,
          } : undefined,
        },
        capturedAt: new Date().toISOString(),
        version: '2.1',
      };
      
      // Update scene metadata with separate stream data
      const sceneId = parseInt(recoveredData.sceneId, 10);
      if (!isNaN(sceneId)) {
        await projectsManager.updateScene(sceneId, {
          status: 'recorded',
          duration: recoveredData.duration,
          micEnabled: recoveredData.micEnabled,
          metadata: separateStreamMetadata,
          videoUrl: uploadedScreenSegments[0]?.key || uploadedWebcamSegments[0]?.key || undefined,
          // Do NOT reset analysisStatus here - preserve existing analysis
          // Analysis is only reset when actually retaking (handleReRecordScene)
        });
        
        logger.info('[SeparateStreamRecorder] Recovered scene recording uploaded', {
          sceneId,
          screenSegments: uploadedScreenSegments.length,
          webcamSegments: uploadedWebcamSegments.length,
          audioSegments: uploadedAudioSegments.length,
        });
        
        callbacks?.onUploadComplete?.(recoveredData.sceneId);
      }
      
      // Delete from IndexedDB after successful upload
      if (recoveredData.id) {
        await deletePendingRecording(recoveredData.id);
        logger.info('[SeparateStreamRecorder] Deleted recovered recording from IndexedDB');
      }
      
    } catch (error: any) {
      logger.error('[SeparateStreamRecorder] Recovered recording upload failed', error);
      const errorMsg = error.message || 'Failed to upload recovered recording';
      setState(prev => ({ ...prev, error: errorMsg }));
      callbacks?.onUploadError?.(errorMsg);
      throw error; // Re-throw so caller knows upload failed
    }
  }, [callbacks]);

  // Clear pending recording (for re-record or discard)
  const clearPendingRecording = useCallback(async (): Promise<void> => {
    // Revoke blob URLs to free memory
    if (state.pendingRecording) {
      if (state.pendingRecording.screenBlobUrl) {
        URL.revokeObjectURL(state.pendingRecording.screenBlobUrl);
      }
      if (state.pendingRecording.webcamBlobUrl) {
        URL.revokeObjectURL(state.pendingRecording.webcamBlobUrl);
      }
      if (state.pendingRecording.audioBlobUrl) {
        URL.revokeObjectURL(state.pendingRecording.audioBlobUrl);
      }
      // Revoke per-segment audio blob URLs
      if (state.pendingRecording.audioSegmentBlobUrls) {
        state.pendingRecording.audioSegmentBlobUrls.forEach(seg => {
          URL.revokeObjectURL(seg.blobUrl);
        });
      }
      
      // Delete from IndexedDB
      if (state.pendingRecording.sceneId) {
        try {
          const pendingRow = await getPendingRecordingBySceneId(state.pendingRecording.sceneId);
          if (pendingRow) {
            await deletePendingRecording(pendingRow.id);
            logger.info('[SeparateStreamRecorder] Deleted pending recording from IndexedDB');
          }
        } catch (idbError) {
          logger.warn('[SeparateStreamRecorder] Failed to delete from IndexedDB (non-critical)', idbError);
        }
      }
    }
    
    // Cleanup refs
    screenRecorderRef.current = null;
    webcamRecorderRef.current = null;
    audioRecorderRef.current = null;
    screenChunksRef.current = [];
    webcamChunksRef.current = [];
    audioChunksRef.current = [];
    screenSegmentsRef.current = [];
    webcamSegmentsRef.current = [];
    audioSegmentsRef.current = [];
    screenSegmentBlobsRef.current.clear();
    webcamSegmentBlobsRef.current.clear();
    audioSegmentBlobsRef.current.clear();
    screenActiveRef.current = false;
    webcamActiveRef.current = false;
    audioActiveRef.current = false;
    originalScreenStreamRef.current = null;
    originalWebcamStreamRef.current = null;
    originalMicStreamRef.current = null;
    originalSystemAudioStreamRef.current = null;
    
    if (mergedAudioStreamRef.current) {
      mergedAudioStreamRef.current.getTracks().forEach(t => t.stop());
      mergedAudioStreamRef.current = null;
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    
    setState(prev => ({ ...prev, pendingRecording: null }));
    
    logger.info('[SeparateStreamRecorder] Pending recording cleared');
  }, [state.pendingRecording]);

  const pauseRecording = useCallback(() => {
    if (screenRecorderRef.current?.state === 'recording') {
      screenRecorderRef.current.pause();
    }
    if (webcamRecorderRef.current?.state === 'recording') {
      webcamRecorderRef.current.pause();
    }
    if (audioRecorderRef.current?.state === 'recording') {
      audioRecorderRef.current.pause();
    }
    
    pauseStartTimeRef.current = Date.now();
    setState(prev => ({ ...prev, isPaused: true }));
    
    logger.info('[SeparateStreamRecorder] Recording paused');
  }, []);

  const resumeRecording = useCallback(() => {
    if (pauseStartTimeRef.current) {
      totalPausedDurationRef.current += Date.now() - pauseStartTimeRef.current;
      pauseStartTimeRef.current = null;
    }
    
    if (screenRecorderRef.current?.state === 'paused') {
      screenRecorderRef.current.resume();
    }
    if (webcamRecorderRef.current?.state === 'paused') {
      webcamRecorderRef.current.resume();
    }
    if (audioRecorderRef.current?.state === 'paused') {
      audioRecorderRef.current.resume();
    }
    
    setState(prev => ({ ...prev, isPaused: false }));
    
    logger.info('[SeparateStreamRecorder] Recording resumed');
  }, []);

  const abortRecording = useCallback(async () => {
    logger.info('[SeparateStreamRecorder] Aborting recording');
    
    const recordingIdToCleanup = recordingIdRef.current;
    
    separateStreamUploader.abortAll();
    
    if (screenRecorderRef.current && screenRecorderRef.current.state !== 'inactive') {
      try {
        screenRecorderRef.current.stop();
      } catch (e) {
      }
    }
    
    if (webcamRecorderRef.current && webcamRecorderRef.current.state !== 'inactive') {
      try {
        webcamRecorderRef.current.stop();
      } catch (e) {
      }
    }
    
    screenRecorderRef.current = null;
    webcamRecorderRef.current = null;
    screenChunksRef.current = [];
    webcamChunksRef.current = [];
    recordingStartTimeRef.current = null;
    pauseStartTimeRef.current = null;
    
    // Cleanup AudioContext
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    totalPausedDurationRef.current = 0;
    
    setState({
      isRecording: false,
      isPaused: false,
      recordingId: null,
      recordingTime: 0,
      uploadProgress: { screen: null, webcam: null, audio: null },
      error: null,
      streamActive: { screen: false, webcam: false, audio: false },
      pendingRecording: null,
    });
    
    if (recordingIdToCleanup) {
      try {
        await blankRecordingService.delete(recordingIdToCleanup);
        logger.info('[SeparateStreamRecorder] Backend recording cleaned up', { recordingId: recordingIdToCleanup });
      } catch (error: any) {
        logger.warn('[SeparateStreamRecorder] Backend cleanup failed', { 
          recordingId: recordingIdToCleanup, 
          error: error.message 
        });
      }
    }
    
    recordingIdRef.current = null;
    uploadUrlsRef.current = null;
    layoutRef.current = null;
    screenSegmentsRef.current = [];
    webcamSegmentsRef.current = [];
    screenActiveRef.current = false;
    webcamActiveRef.current = false;
    
    logger.info('[SeparateStreamRecorder] Recording aborted and cleaned up');
  }, []);

  // Toggle stream on/off during recording - stops/starts MediaRecorder and saves segment blobs
  const toggleStream = useCallback(async (
    streamType: 'screen' | 'webcam',
    active: boolean,
    options?: {
      micStream?: MediaStream | null;
      position?: { x: number; y: number; width: number; height: number };
      stream?: MediaStream | null;
      roundedWebcam?: boolean; // Update webcam shape during recording
      mirroredWebcam?: boolean; // Update webcam mirror state during recording
      avatarWebcam?: boolean; // Update avatar state during recording
      avatarUrl?: string; // Update avatar URL during recording
    }
  ): Promise<void> => {
    // Update roundedWebcam ref if provided
    if (options?.roundedWebcam !== undefined) {
      roundedWebcamRef.current = options.roundedWebcam;
    }
    if (options?.mirroredWebcam !== undefined) {
      mirroredWebcamRef.current = options.mirroredWebcam;
    }
    // Update avatar refs if provided
    if (options?.avatarWebcam !== undefined) {
      avatarWebcamRef.current = options.avatarWebcam;
    }
    if (options?.avatarUrl !== undefined) {
      avatarUrlRef.current = options.avatarUrl;
    }
    if (!state.isRecording || !recordingStartTimeRef.current) {
      logger.warn('[SeparateStreamRecorder] Cannot toggle stream - not recording');
      return;
    }

    const currentTime = getRecordingTime();
    const segmentsRef = streamType === 'screen' ? screenSegmentsRef : webcamSegmentsRef;
    const activeRef = streamType === 'screen' ? screenActiveRef : webcamActiveRef;
    const recorderRef = streamType === 'screen' ? screenRecorderRef : webcamRecorderRef;
    const chunksRef = streamType === 'screen' ? screenChunksRef : webcamChunksRef;
    const blobsRef = streamType === 'screen' ? screenSegmentBlobsRef : webcamSegmentBlobsRef;
    const position = options?.position;
    
    
    if (!active && activeRef.current) {
      // ========== STREAM TURNING OFF ==========
      // 1. Stop the MediaRecorder and save the current segment's blob
      const lastSegment = segmentsRef.current[segmentsRef.current.length - 1];
      
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        await new Promise<void>((resolve) => {
          recorderRef.current!.onstop = () => {
            resolve();
          };
          recorderRef.current!.stop();
        });
      }
      
      // 2. Save the blob for this segment
      if (lastSegment && chunksRef.current.length > 0) {
        const mimeType = getSupportedMimeType(true) || 'video/webm';
        const segmentBlob = new Blob(chunksRef.current, { type: mimeType });
        blobsRef.current.set(lastSegment.segmentIndex, segmentBlob);
        
        // Close the segment
        lastSegment.tsEnd = currentTime;
      }
      
      // 3. Clear chunks for next segment
      chunksRef.current = [];
      recorderRef.current = null;
      activeRef.current = false;
      
    } else if (active && !activeRef.current) {
      // ========== STREAM TURNING ON ==========
      // Update original stream ref if a new stream is provided (for mid-recording stream additions)
      if (options?.stream) {
        if (streamType === 'screen') {
          originalScreenStreamRef.current = options.stream;
        } else {
          originalWebcamStreamRef.current = options.stream;
        }
      }
      
      // 1. Create new segment with next index and current position
      const newSegmentIndex = segmentsRef.current.length;
      const hasAudio = streamType === 'screen' ? screenHasAudioRef.current : webcamHasAudioRef.current;
      
      const newSegment: StreamSegment = {
        segmentIndex: newSegmentIndex,
        tsStart: currentTime,
        tsEnd: null,
        hasAudio,
        uploadStatus: 'pending',
      };
      
      // Add position data if provided
      if (position) {
        newSegment.x = position.x;
        newSegment.y = position.y;
        newSegment.width = position.width;
        newSegment.height = position.height;
      }
      
      // Add shape data based on stream type
      if (streamType === 'webcam') {
        const isRounded = roundedWebcamRef.current;
        newSegment.isCircle = isRounded;
        newSegment.cornerRadius = edgeToEdgeRef.current ? 0 : (isRounded && position 
          ? Math.min(position.width, position.height) / 2 
          : undefined);
        newSegment.isMirrored = mirroredWebcamRef.current;
        // Add avatar properties for webcam
        const isAvatar = avatarWebcamRef.current;
        newSegment.isAvatar = isAvatar;
        newSegment.avatarUrl = isAvatar ? avatarUrlRef.current || undefined : undefined;
      } else {
        // Screen stream - always rectangular with slight corner radius
        newSegment.isCircle = false;
        newSegment.cornerRadius = screenCornerRadiusRef.current;
      }
      
      segmentsRef.current.push(newSegment);
      
      if (!sceneIdRef.current) {
        blankSessionPersistence.onSegmentStart(streamType, newSegmentIndex);
      }
      
      // 2. Create new MediaRecorder for this segment
      const originalStream = streamType === 'screen' ? originalScreenStreamRef.current : originalWebcamStreamRef.current;
      
      if (originalStream) {
        // Clone the stream for the new recorder
        const streamForRecorder = originalStream.clone();
        
        // For screen stream, add audio mixing
        if (streamType === 'screen') {
          // Add system audio and mic audio (same logic as initial start)
          const audioContext = new AudioContext();
          const destination = audioContext.createMediaStreamDestination();
          
          // Add existing audio tracks from screen
          streamForRecorder.getAudioTracks().forEach(track => {
            const tempStream = new MediaStream([track]);
            const source = audioContext.createMediaStreamSource(tempStream);
            source.connect(destination);
          });
          
          // Add system audio if available
          if (originalSystemAudioStreamRef.current) {
            originalSystemAudioStreamRef.current.getAudioTracks().forEach(track => {
              const tempStream = new MediaStream([track]);
              const source = audioContext.createMediaStreamSource(tempStream);
              source.connect(destination);
            });
          }
          
          // Add mic audio
          if (originalMicStreamRef.current) {
            originalMicStreamRef.current.getAudioTracks().forEach(track => {
              const tempStream = new MediaStream([track]);
              const source = audioContext.createMediaStreamSource(tempStream);
              source.connect(destination);
            });
          }
          
          const mergedAudioTrack = destination.stream.getAudioTracks()[0];
          if (mergedAudioTrack) {
            // Create stream with video + merged audio
            const videoTrack = streamForRecorder.getVideoTracks()[0];
            const finalStream = new MediaStream([videoTrack, mergedAudioTrack]);
            
            const recorder = createMediaRecorder(
              finalStream,
              recordingQualityRef.current,
              (blob) => {
                chunksRef.current.push(blob);
                if (!sceneIdRef.current) {
                  const currentSegment = segmentsRef.current[segmentsRef.current.length - 1];
                  blankSessionPersistence.bufferChunk(streamType, currentSegment?.segmentIndex ?? 0, blob);
                }
              }
            );
            recorderRef.current = recorder;
            recorder.start(SEPARATE_STREAM_TIMESLICE);
          }
        } else {
          // Webcam - need to add mic audio if screen is not active (since mic goes to whichever stream is primary)
          let finalWebcamStream = streamForRecorder;
          
          // If screen is not active, webcam becomes primary and gets the mic audio
          if (!screenActiveRef.current && originalMicStreamRef.current) {
            const audioContext = new AudioContext();
            const destination = audioContext.createMediaStreamDestination();
            
            // Add mic audio
            originalMicStreamRef.current.getAudioTracks().forEach(track => {
              const tempStream = new MediaStream([track]);
              const source = audioContext.createMediaStreamSource(tempStream);
              source.connect(destination);
            });
            
            const mergedAudioTrack = destination.stream.getAudioTracks()[0];
            if (mergedAudioTrack) {
              const videoTrack = streamForRecorder.getVideoTracks()[0];
              finalWebcamStream = new MediaStream([videoTrack, mergedAudioTrack]);
              // Update segment to indicate it has audio
              const lastSegment = segmentsRef.current[segmentsRef.current.length - 1];
              if (lastSegment) {
                lastSegment.hasAudio = true;
              }
            }
          }
          
          const recorder = createMediaRecorder(
            finalWebcamStream,
            recordingQualityRef.current,
            (blob) => {
              chunksRef.current.push(blob);
              if (!sceneIdRef.current) {
                const currentSegment = segmentsRef.current[segmentsRef.current.length - 1];
                blankSessionPersistence.bufferChunk(streamType, currentSegment?.segmentIndex ?? 0, blob);
              }
            }
          );
          recorderRef.current = recorder;
          recorder.start(SEPARATE_STREAM_TIMESLICE);
        }
        
      } else {
      }
      
      activeRef.current = true;
    } else if (active && activeRef.current && position) {
      // ========== STREAM ALREADY ON BUT POSITION CHANGED ==========
      // This happens when a layout preset is applied that changes position/size
      // We need to close the current segment and start a new one with new position
      const lastSegment = segmentsRef.current[segmentsRef.current.length - 1];
      
      // Check if position actually changed
      const positionChanged = lastSegment && (
        lastSegment.x !== position.x ||
        lastSegment.y !== position.y ||
        lastSegment.width !== position.width ||
        lastSegment.height !== position.height
      );
      
      if (positionChanged) {
        
        // 1. Stop current recorder and save blob
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
          await new Promise<void>((resolve) => {
            recorderRef.current!.onstop = () => {
              resolve();
            };
            recorderRef.current!.stop();
          });
        }
        
        // 2. Save blob and close segment
        if (chunksRef.current.length > 0) {
          const mimeType = getSupportedMimeType(true) || 'video/webm';
          const segmentBlob = new Blob(chunksRef.current, { type: mimeType });
          blobsRef.current.set(lastSegment.segmentIndex, segmentBlob);
        }
        lastSegment.tsEnd = currentTime;
        
        // 3. Clear chunks for next segment
        chunksRef.current = [];
        recorderRef.current = null;
        
        // 4. Create new segment with new position
        const newSegmentIndex = segmentsRef.current.length;
        const hasAudio = streamType === 'screen' ? screenHasAudioRef.current : webcamHasAudioRef.current;
        
        const newSegment: StreamSegment = {
          segmentIndex: newSegmentIndex,
          tsStart: currentTime,
          tsEnd: null,
          hasAudio,
          uploadStatus: 'pending',
          x: position.x,
          y: position.y,
          width: position.width,
          height: position.height,
        };
        
        // Add shape data
        if (streamType === 'webcam') {
          const isRounded = roundedWebcamRef.current;
          const isAvatar = avatarWebcamRef.current;
          newSegment.isCircle = isRounded;
          newSegment.cornerRadius = edgeToEdgeRef.current ? 0 : (isRounded ? Math.min(position.width, position.height) / 2 : undefined);
          newSegment.isMirrored = mirroredWebcamRef.current;
          newSegment.isAvatar = isAvatar;
          newSegment.avatarUrl = isAvatar ? avatarUrlRef.current || undefined : undefined;
        } else {
          newSegment.isCircle = false;
          newSegment.cornerRadius = edgeToEdgeRef.current ? 0 : 12;
        }
        
        segmentsRef.current.push(newSegment);
        
        if (!sceneIdRef.current) {
          blankSessionPersistence.onSegmentStart(streamType, newSegmentIndex);
        }
        
        // 5. Start new recorder
        const originalStreamRef = streamType === 'screen' ? originalScreenStreamRef : originalWebcamStreamRef;
        if (originalStreamRef.current) {
          const videoOnlyTracks = originalStreamRef.current.getVideoTracks();
          const videoOnlyStream = new MediaStream(videoOnlyTracks);
          
          const recorder = createMediaRecorder(
            videoOnlyStream,
            recordingQualityRef.current,
            (blob) => {
              chunksRef.current.push(blob);
              if (!sceneIdRef.current) {
                const currentSegment = segmentsRef.current[segmentsRef.current.length - 1];
                blankSessionPersistence.bufferChunk(streamType, currentSegment?.segmentIndex ?? 0, blob);
              }
            }
          );
          
          if (recorder) {
            recorderRef.current = recorder;
            recorder.start(SEPARATE_STREAM_TIMESLICE);
          }
        }
      } else {
      }
    }
    
    // Update state
    setState(prev => ({
      ...prev,
      streamActive: {
        ...prev.streamActive,
        [streamType]: active,
      },
    }));
    
    // Update layout with current segments
    if (layoutRef.current) {
      if (streamType === 'screen') {
        // Create screen layout if it doesn't exist and we're enabling screen
        if (!layoutRef.current.screen && active && options?.position) {
          layoutRef.current.screen = {
            x: options.position.x,
            y: options.position.y,
            width: options.position.width,
            height: options.position.height,
            visible: true,
            segments: [],
          };
        }
        if (layoutRef.current.screen) {
          layoutRef.current.screen.segments = [...screenSegmentsRef.current];
          layoutRef.current.screen.visible = active;
          (layoutRef.current.screen as any).cornerRadius = screenCornerRadiusRef.current;
        }
      } else if (streamType === 'webcam') {
        // Create webcam layout if it doesn't exist and we're enabling webcam
        if (!layoutRef.current.webcam && active && options?.position) {
          layoutRef.current.webcam = {
            x: options.position.x,
            y: options.position.y,
            width: options.position.width,
            height: options.position.height,
            visible: true,
            segments: [],
          };
        }
        if (layoutRef.current.webcam) {
          layoutRef.current.webcam.segments = [...webcamSegmentsRef.current];
          layoutRef.current.webcam.visible = active;
          (layoutRef.current.webcam as any).isAvatar = avatarWebcamRef.current;
          if (avatarWebcamRef.current && avatarUrlRef.current) {
            (layoutRef.current.webcam as any).avatarUrl = avatarUrlRef.current;
          }
          const isRounded = roundedWebcamRef.current;
          const wcWidth = layoutRef.current.webcam.width ?? 308;
          const wcHeight = layoutRef.current.webcam.height ?? 344;
          (layoutRef.current.webcam as any).isCircle = isRounded;
          (layoutRef.current.webcam as any).cornerRadius = edgeToEdgeRef.current ? 0 : (isRounded ? Math.min(wcWidth, wcHeight) / 2 : 0);
          (layoutRef.current.webcam as any).shape = isRounded ? 'rounded' : null;
        }
      }
    }
  }, [state.isRecording, getRecordingTime, createMediaRecorder]);

  const interruptRecording = useCallback(async () => {
    if (!state.isRecording) return;
    logger.info('[SeparateStreamRecorder] Interrupting recording for navigation');
    
    const duration = getRecordingTime();
    const videoMimeType = getSupportedMimeType(true) || 'video/webm';
    const audioMimeType = 'audio/webm;codecs=opus';
    
    const assembleBlob = (
      chunksRef: React.MutableRefObject<Blob[]>,
      segmentsRef: React.MutableRefObject<any[]>,
      blobsRef: React.MutableRefObject<Map<number, Blob>>,
      mime: string,
    ) => {
      const lastSeg = segmentsRef.current[segmentsRef.current.length - 1];
      if (lastSeg && chunksRef.current.length > 0) {
        const blob = new Blob(chunksRef.current, { type: mime });
        blobsRef.current.set(lastSeg.segmentIndex, blob);
        if (lastSeg.tsEnd === null) lastSeg.tsEnd = duration;
      }
    };
    
    if (screenRecorderRef.current && screenRecorderRef.current.state !== 'inactive') {
      try { screenRecorderRef.current.stop(); } catch (e) {}
    }
    assembleBlob(screenChunksRef, screenSegmentsRef, screenSegmentBlobsRef, videoMimeType);
    
    if (webcamRecorderRef.current && webcamRecorderRef.current.state !== 'inactive') {
      try { webcamRecorderRef.current.stop(); } catch (e) {}
    }
    assembleBlob(webcamChunksRef, webcamSegmentsRef, webcamSegmentBlobsRef, videoMimeType);
    
    if (audioRecorderRef.current && audioRecorderRef.current.state !== 'inactive') {
      try { audioRecorderRef.current.stop(); } catch (e) {}
    }
    assembleBlob(audioChunksRef, audioSegmentsRef, audioSegmentBlobsRef, audioMimeType);
    
    for (const seg of screenSegmentsRef.current) { if (seg.tsEnd === null) seg.tsEnd = duration; }
    for (const seg of webcamSegmentsRef.current) { if (seg.tsEnd === null) seg.tsEnd = duration; }
    for (const seg of audioSegmentsRef.current) { if (seg.tsEnd === null) seg.tsEnd = duration; }
    
    const filterSegmentsWithData = (
      segmentsRef: React.MutableRefObject<any[]>,
      blobsRef: React.MutableRefObject<Map<number, Blob>>,
    ) => {
      return segmentsRef.current
        .filter(s => blobsRef.current.has(s.segmentIndex))
        .map(s => ({ ...s }));
    };
    
    if (!sceneIdRef.current) {
      try {
        await blankSessionPersistence.stopSession(
          filterSegmentsWithData(screenSegmentsRef, screenSegmentBlobsRef),
          filterSegmentsWithData(webcamSegmentsRef, webcamSegmentBlobsRef),
          filterSegmentsWithData(audioSegmentsRef, audioSegmentBlobsRef)
        );
      } catch (err) {
        logger.warn('[SeparateStreamRecorder] Failed to persist interrupted session', err);
      }
    }
    
    setState({
      isRecording: false,
      isPaused: false,
      recordingId: null,
      recordingTime: 0,
      uploadProgress: { screen: null, webcam: null, audio: null },
      error: null,
      streamActive: { screen: false, webcam: false, audio: false },
      pendingRecording: null,
    });
    
    screenRecorderRef.current = null;
    webcamRecorderRef.current = null;
    audioRecorderRef.current = null;
    screenChunksRef.current = [];
    webcamChunksRef.current = [];
    audioChunksRef.current = [];
    screenSegmentsRef.current = [];
    webcamSegmentsRef.current = [];
    audioSegmentsRef.current = [];
    screenSegmentBlobsRef.current.clear();
    webcamSegmentBlobsRef.current.clear();
    audioSegmentBlobsRef.current.clear();
    screenActiveRef.current = false;
    webcamActiveRef.current = false;
    recordingStartTimeRef.current = null;
    recordingIdRef.current = null;
    
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    
    logger.info('[SeparateStreamRecorder] Recording interrupted and state cleaned up');
  }, [state.isRecording, getRecordingTime]);

  const updateBackgroundKey = useCallback((imageKey: string) => {
    if (layoutRef.current?.background) {
      layoutRef.current.background = {
        ...layoutRef.current.background,
        imageKey,
      };
      logger.info('[SeparateStreamRecorder] Updated background imageKey:', imageKey);
    }
  }, []);

  return {
    state,
    startRecording,
    stopRecording,
    uploadRecording,
    uploadFromRecoveredData,
    clearPendingRecording,
    abortRecording,
    interruptRecording,
    pauseRecording,
    resumeRecording,
    getRecordingTime,
    toggleStream,
    updateBackgroundKey,
  };
}
